import { flush as trackuxFlush, track as trackuxTrack } from 'trackux'

const MEASUREMENT_ID = process.env.GATSBY_GA_MEASUREMENT_ID
const DEBUG_MODE = process.env.GATSBY_GA_DEBUG_MODE === 'true'
const DEFAULT_TRACKUX_FLUSH_TIMEOUT_MS = 900

function sanitizeParams(params = {}) {
  return Object.entries(params).reduce((accumulator, [key, value]) => {
    if (value === undefined || value === null || value === '') {
      return accumulator
    }

    accumulator[key] = value
    return accumulator
  }, {})
}

function getWindowLocation(location) {
  if (location) return location
  if (typeof window !== 'undefined') return window.location
  return null
}

export function isAnalyticsEnabled() {
  return typeof window !== 'undefined' && Boolean(MEASUREMENT_ID)
}

export function canTrack() {
  return isAnalyticsEnabled() && typeof window.gtag === 'function'
}

export function trackEvent(eventName, params = {}) {
  if (!eventName) return false

  const sanitizedParams = sanitizeParams(params)
  let didTrack = false

  if (typeof window !== 'undefined') {
    try {
      trackuxTrack(eventName, sanitizedParams)
      didTrack = true
    } catch (error) {
      if (DEBUG_MODE) {
        console.warn('TrackUX event failed', eventName, error)
      }
    }
  }

  if (canTrack()) {
    window.gtag('event', eventName, {
      ...sanitizedParams,
      ...(DEBUG_MODE ? { debug_mode: true } : {}),
    })
    didTrack = true
  }

  return didTrack
}

export function flushTrackedEvents(timeoutMs = DEFAULT_TRACKUX_FLUSH_TIMEOUT_MS) {
  if (typeof window === 'undefined') return Promise.resolve(false)

  let timeoutId

  return Promise.race([
    Promise.resolve()
      .then(() => trackuxFlush())
      .then(() => true)
      .catch((error) => {
        if (DEBUG_MODE) {
          console.warn('TrackUX flush failed', error)
        }
        return false
      }),
    new Promise((resolve) => {
      timeoutId = window.setTimeout(() => resolve(false), timeoutMs)
    }),
  ]).finally(() => {
    if (timeoutId) window.clearTimeout(timeoutId)
  })
}

export function trackPageView(location) {
  const resolvedLocation = getWindowLocation(location)

  if (!canTrack() || !resolvedLocation) return false

  const pagePath = `${resolvedLocation.pathname || ''}${resolvedLocation.search || ''}${resolvedLocation.hash || ''}`
  const pageLocation =
    resolvedLocation.href ||
    (typeof window !== 'undefined' ? window.location.href : pagePath)

  return trackEvent('page_view', {
    page_title: typeof document !== 'undefined' ? document.title : undefined,
    page_location: pageLocation,
    page_path: pagePath,
  })
}

export function getAnalyticsMeasurementId() {
  return MEASUREMENT_ID
}
